(function () {
  "use strict";

  // Wait for GSAP to be available
  function checkGSAP() {
    let gsapLib = null;

    // Check multiple ways GSAP might be available
    if (typeof gsap !== "undefined") {
      gsapLib = gsap;
    } else if (typeof window.gsap !== "undefined") {
      gsapLib = window.gsap;
    } else if (window.SOJ_GSAP_ANIMATIONS && window.SOJ_GSAP_ANIMATIONS.gsap) {
      gsapLib = window.SOJ_GSAP_ANIMATIONS.gsap;
    }

    if (!gsapLib) {
      // Retry after a short delay if GSAP isn't loaded yet
      setTimeout(checkGSAP, 100);
      return;
    }

    // Initialize hero animations once GSAP is available
    initHeroAnimations(gsapLib);
  }

  /**
   * Check if mobile viewport
   * @returns {boolean}
   */
  function isMobile() {
    return window.innerWidth < 768;
  }

  /**
   * Initialize mobile hero animations (simple fade in/out)
   * @param {Object} gsapLib - GSAP library instance
   */
  function initMobileAnimations(gsapLib) {
    const heroBlock = document.querySelector(".hero");
    if (!heroBlock) return;

    // Mobile images to loop through (must match SCSS mobile display settings)
    const mobileImages = [
      ".image1",
      ".image2",
      ".image3",
      ".image5",
    ];

    // Set initial state - all images hidden
    mobileImages.forEach((image) => {
      gsapLib.set(image, { opacity: 0 });
    });

    // Create timeline
    const timeline = gsapLib.timeline({ repeat: -1 });

    const fadeDuration = 1.4;
    const holdDuration = 0.3;

    // Animate first image in on page load
    timeline.fromTo(
      mobileImages[0],
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: fadeDuration,
        ease: "power2.out",
      }
    );

    // Hold first image visible
    timeline.to({}, { duration: holdDuration });

    // Animate through each image
    mobileImages.forEach((currentImage, index) => {
      const nextIndex = (index + 1) % mobileImages.length;
      const nextImage = mobileImages[nextIndex];

      // Fade out current image
      timeline.to(currentImage, {
        opacity: 0,
        duration: fadeDuration,
        ease: "power2.in",
      });

      // Fade in next image
      timeline.to(
        nextImage,
        {
          opacity: 1,
          duration: fadeDuration,
          ease: "power2.out",
        },
        "-=0.4" // Start slightly before fade out ends
      );

      // Hold next image visible
      timeline.to({}, { duration: holdDuration });
    });

    return timeline;
  }

  /**
   * Initialize desktop hero image group animations
   * @param {Object} gsapLib - GSAP library instance
   */
  function initDesktopAnimations(gsapLib) {
    const heroBlock = document.querySelector(".hero");
    if (!heroBlock) return;

    // Define image groups
    const groups = [
      [".image1", ".image2", ".image3", ".image4", ".image5"], // Group 1
      [".image6", ".image7", ".image8", ".image9", ".image10"], // Group 2
      [".image11", ".image12", ".image13", ".image14", ".image15"], // Group 3
      [".image16", ".image17", ".image18", ".image19", ".image20"], // Group 4
      [".image21", ".image22", ".image23", ".image24", ".image25"], // Group 5
      [".image26", ".image27", ".image28", ".image29", ".image30"], // Group 6
    ];

    // Define which images should animate from right to left (using x position)
    // Add image class names here (e.g., ".image1", ".image10", etc.)
    const rightToLeftImages = [
      ".image1", ".image5", ".image9", ".image10", ".image13", ".image14", ".image18", ".image20", ".image21", "image25", ".image29", ".image30"
      // Add more images here as needed
    ];

    // Define which images should animate from left to right (using x position)
    // Add image class names here (e.g., ".image3", ".image15", etc.)
    const leftToRightImages = [
      ".image2", ".image6", ".image15", ".image19", '.image22', ".image26"
      // Add more images here as needed
    ];

    // Set initial state - all groups are hidden
    groups.forEach((group) => {
      group.forEach((imageSelector) => {
        if (rightToLeftImages.includes(imageSelector)) {
          // Right-to-left images start offset to the right
          gsapLib.set(imageSelector, { opacity: 0, y: 0, x: 40 });
        } else if (leftToRightImages.includes(imageSelector)) {
          // Left-to-right images start offset to the left
          gsapLib.set(imageSelector, { opacity: 0, y: 0, x: -40 });
        } else {
          // Default images start offset below
          gsapLib.set(imageSelector, { opacity: 0, y: 40, x: 0 });
        }
      });
    });

    // Create master timeline that loops infinitely
    const masterTimeline = gsapLib.timeline({ repeat: -1 });

    // Animation duration settings
    const fadeOutDuration = 1.4;
    const fadeInDuration = 1.4;
    const holdDuration = 0; // Time to hold each group visible (0 for constant animation)
    const staggerDelay = 0.4; // Delay between each image appearing

    // Animate first group in on page load
    groups[0].forEach((imageSelector, index) => {
      const startPosition = index === 0 ? 0 : `-=${fadeInDuration - staggerDelay}`;

      if (rightToLeftImages.includes(imageSelector)) {
        // Animate from right to left with fade in
        masterTimeline.fromTo(
          imageSelector,
          {
            opacity: 0,
            y: 0,
            x: 40,
          },
          {
            opacity: 1,
            y: 0,
            x: 0,
            duration: fadeInDuration,
            ease: "power2.out",
          },
          startPosition
        );
      } else if (leftToRightImages.includes(imageSelector)) {
        // Animate from left to right with fade in
        masterTimeline.fromTo(
          imageSelector,
          {
            opacity: 0,
            y: 0,
            x: -40,
          },
          {
            opacity: 1,
            y: 0,
            x: 0,
            duration: fadeInDuration,
            ease: "power2.out",
          },
          startPosition
        );
      } else {
        // Animate from below (default) with fade in
        masterTimeline.fromTo(
          imageSelector,
          {
            opacity: 0,
            y: 40,
            x: 0,
          },
          {
            opacity: 1,
            y: 0,
            x: 0,
            duration: fadeInDuration,
            ease: "power2.out",
          },
          startPosition
        );
      }
    });

    // Hold the first group visible
    masterTimeline.to({}, { duration: holdDuration });

    // Animate through each group (will loop back to first group automatically)
    groups.forEach((currentGroup, groupIndex) => {
      const nextGroupIndex = (groupIndex + 1) % groups.length;
      const nextGroup = groups[nextGroupIndex];

      // Fade out current group - all images fade out
      currentGroup.forEach((imageSelector, index) => {
        const fadeOutPosition = index === 0 ? ">" : `-=${fadeOutDuration - staggerDelay}`;

        // All images fade out
        masterTimeline.to(
          imageSelector,
          {
            opacity: 0,
            duration: fadeOutDuration,
            ease: "power2.in",
          },
          fadeOutPosition
        );
      });

      // Fade in next group - check direction for each image
      nextGroup.forEach((imageSelector, imageIndex) => {
        // Calculate proper timing:
        // First image overlaps with the last part of fade out
        // Subsequent images stagger from the first
        let fadeInPosition;
        if (imageIndex === 0) {
          // Start when fade out is mostly complete (0.3s overlap for smooth transition)
          fadeInPosition = `-=0.3`;
        } else {
          // Stagger from previous image
          fadeInPosition = `-=${fadeInDuration - staggerDelay}`;
        }

        if (rightToLeftImages.includes(imageSelector)) {
          // Fade in from right to left
          masterTimeline.fromTo(
            imageSelector,
            {
              opacity: 0,
              y: 0,
              x: 40,
            },
            {
              opacity: 1,
              y: 0,
              x: 0,
              duration: fadeInDuration,
              ease: "power2.out",
            },
            fadeInPosition
          );
        } else if (leftToRightImages.includes(imageSelector)) {
          // Fade in from left to right
          masterTimeline.fromTo(
            imageSelector,
            {
              opacity: 0,
              y: 0,
              x: -40,
            },
            {
              opacity: 1,
              y: 0,
              x: 0,
              duration: fadeInDuration,
              ease: "power2.out",
            },
            fadeInPosition
          );
        } else {
          // Fade in from below (default)
          masterTimeline.fromTo(
            imageSelector,
            {
              opacity: 0,
              y: 40,
              x: 0,
            },
            {
              opacity: 1,
              y: 0,
              x: 0,
              duration: fadeInDuration,
              ease: "power2.out",
            },
            fadeInPosition
          );
        }
      });

      // Hold the next group visible
      masterTimeline.to({}, { duration: holdDuration });
    });

    return masterTimeline;
  }

  /**
   * Initialize opening text span animations
   * @param {Object} gsapLib - GSAP library instance
   */
  function initOpeningTextAnimation(gsapLib) {
    const openingText = document.querySelector(".opening_text");
    if (!openingText) return;

    const spans = openingText.querySelectorAll(".carousel-word");
    if (spans.length === 0) return;

    const afterOpening = document.querySelector(".after_opening");

    // Set initial state - all spans hidden below their position
    gsapLib.set(spans, { opacity: 0, y: 30 });

    // Set initial state for after_opening - hidden and offset to the left
    if (afterOpening) {
      gsapLib.set(afterOpening, { opacity: 0, visibility: "hidden", x: -40 });
    }

    // Create timeline for carousel animation (one span at a time, no loop)
    const timeline = gsapLib.timeline();

    // Reveal first span from bottom up
    timeline.fromTo(
      spans[0],
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
    );

    // Wait 0.8 seconds
    timeline.to({}, { duration: 0.5 });

    // Hide first span (move up and fade out)
    timeline.to(spans[0], { opacity: 0, y: -30, duration: 0.4, ease: "power2.in" });

    // Reveal second span from bottom up
    timeline.fromTo(
      spans[1],
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
      "-=0.2" // Slight overlap for smoother transition
    );

    // Wait 0.8 seconds
    timeline.to({}, { duration: 0.5 });

    // Hide second span (move up and fade out)
    timeline.to(spans[1], { opacity: 0, y: -30, duration: 0.4, ease: "power2.in" });

    // Reveal third span from bottom up (stays visible)
    timeline.fromTo(
      spans[2],
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
      "-=0.2" // Slight overlap for smoother transition
    );

    // Wait 0.8 seconds before fading out the entire div
    timeline.to({}, { duration: 0.5 });

    // Fade out the entire opening_text div
    timeline.to(openingText, {
      opacity: 0,
      visibility: "hidden",
      duration: 0.5,
      ease: "power2.inOut"
    });

    if (afterOpening) {
      timeline.to(
        afterOpening,
        {
          opacity: 1,
          visibility: "visible",
          x: 0,
          duration: 0.7,
          ease: "power2.out"
        },
        "-=0.3" // Slight overlap for smoother transition
      );
    }

    // Add a label when after_opening starts to become visible
    timeline.add("afterOpeningStart", "-=0.5");

    return timeline;
  }

  /**
   * Initialize hero image group animations
   * @param {Object} gsapLib - GSAP library instance
   */
  function initHeroAnimations(gsapLib) {
    let currentTimeline = null;

    function setupAnimation(shouldAutoPlay = false) {
      // Kill existing animation if any
      if (currentTimeline) {
        currentTimeline.kill();
        currentTimeline = null;
      }

      // Initialize appropriate animation based on viewport
      if (isMobile()) {
        currentTimeline = initMobileAnimations(gsapLib);
      } else {
        currentTimeline = initDesktopAnimations(gsapLib);
      }

      if (currentTimeline) {
        if (shouldAutoPlay) {
          // Start playing immediately (for resize after initial load)
          currentTimeline.play();
        } else {
          // Pause the timeline initially - it will be started after text animation
          currentTimeline.pause();
        }
      }
    }

    // Initialize opening text animation (runs on all viewports)
    const textTimeline = initOpeningTextAnimation(gsapLib);

    // Initial setup
    setupAnimation();

    // Start image animations when after_opening becomes visible
    if (textTimeline && currentTimeline) {
      // Play image animations at the "afterOpeningStart" label
      textTimeline.call(() => {
        if (currentTimeline) {
          currentTimeline.play();
        }
      }, null, "afterOpeningStart");
    }

    // Handle window resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Auto-play animations after resize (text animation already completed)
        setupAnimation(true);
      }, 250); // Debounce resize
    });
  }

  // Start checking for GSAP
  checkGSAP();
})();
